import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { usePortalStore } from '@/store/portal-store.js';
import type { PortalMode } from '@/store/portal-store.js';
import { makePortalInstance, getPortalRequestInit } from '@edison/api-client-react';
import {
  ArrowUp, ArrowLeft, AlertCircle, Loader2, Lock, ShieldCheck, Shuffle, BarChart2, Clock,
  ChevronRight, ChevronDown, MessageSquare, ExternalLink, CheckCircle2, AlertTriangle, Info, RefreshCw,
} from 'lucide-react';
import { EdisonMark } from '@/components/icons/edison-logo.js';
import { PortalMarkdown } from '@/components/ui/portal-markdown.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { PortalArtefactPanel } from '@/components/ui/portal-artefact-panel.js';
import type { ArtefactRef, ArtefactCitation } from '@/components/ui/portal-artefact-panel.js';

// ── Rating derivation (mirrored in portal-eligibility.tsx) ───────────────────

type Rating = 'strong' | 'moderate' | 'weak' | 'gap';

const RATING_BADGE: Record<Rating, string> = {
  strong:   'bg-green-900/50 text-green-300 border border-green-800/40',
  moderate: 'bg-amber-900/50 text-amber-300 border border-amber-800/40',
  weak:     'bg-orange-900/50 text-orange-300 border border-orange-800/40',
  gap:      'bg-red-900/50 text-red-300 border border-red-800/40',
};

const RATING_LABEL: Record<Rating, string> = {
  strong: 'Strong', moderate: 'Moderate', weak: 'Weak', gap: 'Gap',
};

interface ProjectCardData {
  projectId: string;
  projectName: string;
  tfaLocked: boolean;
  tfaDetermination: string | null;
  tfaProblemStatement: string | null;
  coreActivityCount: number;
  hasExperimentalEvidence: boolean;
  totalArtefacts: number;
}

interface PortfolioProject {
  projectId: string;
  projectName: string;
  tfa: {
    locked: boolean;
    determination: { determination: string } | null;
    problemStatement: string | null;
  } | null;
  coreActivities: unknown[];
  documentationAppendix: {
    testingAndBenchmarking: unknown[];
    experimentation: unknown[];
    githubArtefacts: unknown[];
    projectPlansAndPrds: unknown[];
    aiModelEvaluation: unknown[];
    userFeedback: unknown[];
    other: unknown[];
  };
}

function summarizeProject(p: PortfolioProject): ProjectCardData {
  const d = p.documentationAppendix;
  const totalArtefacts = (
    d.githubArtefacts.length + d.projectPlansAndPrds.length + d.testingAndBenchmarking.length +
    d.experimentation.length + d.aiModelEvaluation.length + d.userFeedback.length + d.other.length
  );
  return {
    projectId: p.projectId,
    projectName: p.projectName,
    tfaLocked: p.tfa?.locked ?? false,
    tfaDetermination: p.tfa?.determination?.determination ?? null,
    tfaProblemStatement: p.tfa?.problemStatement ?? null,
    coreActivityCount: p.coreActivities.length,
    hasExperimentalEvidence: (d.testingAndBenchmarking.length + d.experimentation.length) > 0,
    totalArtefacts,
  };
}

interface Ratings { hypothesis: Rating; systematic: Rating; newKnowledge: Rating; records: Rating; }

function deriveRatings(s: ProjectCardData): Ratings {
  const hypothesis: Rating = s.tfaLocked && s.tfaProblemStatement ? 'strong' :
    s.tfaLocked ? 'moderate' : 'gap';

  const systematic: Rating = s.coreActivityCount > 0 && s.hasExperimentalEvidence ? 'strong' :
    s.coreActivityCount > 0 ? 'moderate' : 'gap';

  const newKnowledge: Rating = s.tfaDetermination === 'uncertain' ? 'strong' :
    s.tfaDetermination === 'partially_uncertain' ? 'moderate' :
    s.tfaLocked ? 'weak' : 'gap';

  const records: Rating = s.totalArtefacts >= 10 ? 'strong' :
    s.totalArtefacts >= 3 ? 'moderate' :
    s.totalArtefacts > 0 ? 'weak' : 'gap';

  return { hypothesis, systematic, newKnowledge, records };
}

// ── Suggestion chips ──────────────────────────────────────────────────────────

interface Suggestion {
  label: string;
  question: string;
  Icon: React.FC<{ className?: string }>;
}

const SUGGESTIONS: Suggestion[] = [
  {
    label: 'Where did they navigate technical uncertainty?',
    question: 'Where did they navigate technical uncertainty? Walk me through the core problem, what was unknown, and how the team worked through it.',
    Icon: ShieldCheck,
  },
  {
    label: 'Pull a random sample of evidence for core activities',
    question: 'What is a representative sample of evidence across the core R&D activities? Show artefacts spanning different activities and types.',
    Icon: Shuffle,
  },
  {
    label: 'Which project is weakest, and why?',
    question: 'Which project in this claim has the weakest evidence position and why? What gaps remain and what could be done to strengthen it?',
    Icon: BarChart2,
  },
  {
    label: 'Reconcile claimed R&D hours to source signals',
    question: 'Reconcile the claimed R&D hours to their source evidence. How were the hours determined, what signals back them up, and are there any gaps?',
    Icon: Clock,
  },
];

// ── Project card ──────────────────────────────────────────────────────────────

function PortfolioProjectCard({
  project,
  basePath,
}: {
  project: ProjectCardData;
  basePath: string;
}) {
  const [, navigate] = useLocation();
  const ratings = deriveRatings(project);
  const gapCount = [ratings.hypothesis, ratings.systematic, ratings.newKnowledge, ratings.records].filter((r) => r === 'gap').length;
  const isReady = gapCount === 0;

  return (
    <button
      onClick={() => navigate(`${basePath}/eligibility?project=${project.projectId}`)}
      className="w-full text-left bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-gray-700 hover:bg-gray-800/60 transition-colors group"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{project.projectName}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              isReady ? 'bg-green-900/60 text-green-300' : 'bg-amber-900/60 text-amber-300'
            }`}>
              {isReady ? 'Audit-ready' : `${gapCount} gap${gapCount > 1 ? 's' : ''}`}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {project.totalArtefacts} artefacts · {project.coreActivityCount} core {project.coreActivityCount === 1 ? 'activity' : 'activities'}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-gray-400 shrink-0 mt-0.5 transition-colors" />
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap gap-1.5 items-center">
          {([
            ['Hypothesis', ratings.hypothesis],
            ['Systematic', ratings.systematic],
            ['New knowledge', ratings.newKnowledge],
            ['Records', ratings.records],
          ] as [string, Rating][]).map(([label, rating]) => (
            <span key={label} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${RATING_BADGE[rating]}`}>
              {RATING_LABEL[rating]} · {label}
            </span>
          ))}
        </div>
        <span className="text-xs text-indigo-400 group-hover:text-indigo-300 transition-colors whitespace-nowrap shrink-0">
          Eligibility report →
        </span>
      </div>
    </button>
  );
}

// ── API shapes ────────────────────────────────────────────────────────────────
//
// Typed-block Ask envelope (Task 7 backend, Task 8 frontend). Field names copied verbatim from
// artifacts/api-server/src/services/auditor-ask/intents/types.ts — this is a hand-mirrored type,
// the portal API client is untyped fetch/axios, so nothing else checks this shape at compile time.
// Figures inside blocks are RAW numbers — this file is the ONLY place that formats them, so a
// mistaken re-format can never silently coexist with a wrong number the model stated.

interface AskScope {
  incomeYear: string;
  projectCount: number;
}

type AskBlock =
  | { type: 'prose'; text: string }
  | { type: 'kpi_block'; title: string; items: Array<{ label: string; value: number | string; unit?: 'aud' | 'hours' | 'count' | 'text' }> }
  | { type: 'evidence_table'; rows: Array<{ artefactId: string; title: string; artefactType: string; date: string | null; sourceDate: string | null; createdAt: string; projectName: string | null; activityName: string | null; githubUrl?: string }> }
  | { type: 'expenditure_table'; labour: Array<{ projectName: string; activityName: string; activityType: string; rdHours: number; rdExpenditure: number }>; nonLabour: Array<{ vendor: string; category: string | null; edisonAmount: number; glAmount: number | null; variancePct: number | null; mappedAccounts: string[]; unmapped: boolean; sourceKind: string }>; totals: { totalRdHours: number; totalRdExpenditure: number; totalNonLabourExpenditure: number; grandTotal: number } }
  | { type: 'reconciliation_table'; labour: unknown; nonLabour: Array<{ vendor: string; edisonAmount: number; glAmount: number | null; varianceAmount: number | null; variancePct: number | null; mappedAccounts: string[]; unmapped: boolean }>; meta: { lastXeroSyncAt: string | null; hasSnapshotData: boolean } }
  | { type: 'criteria_cards'; projects: Array<{ projectId: string; projectName: string; tfaDetermination: string | null; problemStatement: string | null; coreActivities: Array<{ name: string; description: string | null; ipOwnership: string; uncertaintyBases: string[] }>; documents: Array<{ artefactId: string; title: string; artefactType: string }> }> }
  | { type: 'defensibility_block'; projectId: string; projectName: string; checks: Array<{ criterion: string; score: string; rationale: string; gapsSummary: string | null; citations: ArtefactCitation[]; supportingArtefactCount: number }> }
  | { type: 'project_table'; rows: Array<{ projectName: string; tfaDetermination: string | null; rdHours: number; rdExpenditure: number; activities?: Array<{ name: string; activityType: string; ipOwnership: string; rdHours: number; rdExpenditure: number }> }> }
  | { type: 'sdlc_coverage'; title: string; basisNote: string; categories: Array<{ key: string; label: string; hours: number; exampleLabels: string[] }>; unmappedHours: number; totalHours: number }
  | { type: 'sdlc_stage_detail'; stageKey: string; stageLabel: string; basisNote: string; items: Array<{ label: string; hours: number }> }
  | { type: 'composition'; title: string; claimedProjectCount: number; excludedNonRdProjectCount: number; categories: Array<{ key: string; label: string; hours: number; expenditure: number }> }
  | { type: 'ledger_timeline'; projectId: string; projectName: string; current: unknown | null; history: unknown[] }
  | { type: 'refusal'; message: string };

interface AskResponse {
  data: { blocks: AskBlock[]; citations: ArtefactCitation[]; scope: AskScope; conversationId: string };
}

// A stored assistant turn carries the full typed-block payload in `blocks` (0077); user turns and
// pre-0077 assistant rows have `blocks: null` and render from `content` prose.
type StoredBlocks = { blocks: AskBlock[]; citations: ArtefactCitation[]; scope: AskScope };

interface AskHistoryResponse {
  data: {
    conversationId?: string;
    messages: Array<{ role: 'user' | 'assistant'; content: string; blocks: StoredBlocks | null; createdAt: string }>;
  };
}

interface ConversationSummary {
  id: string;
  title: string;
  startedAt: string;
  lastMessageAt: string;
  messageCount: number;
}

interface AskConversationsResponse {
  data: { items: ConversationSummary[] };
}

interface ClaimInfo { name: string; incomeYear: string }

// A user turn always carries `content`. An assistant turn — whether live from POST /ask or restored
// from GET /ask/history (0077 persists the payload) — carries the typed `blocks` plus the
// scope/citations that came with that answer. Only pre-0077 stored assistant rows fall back to
// `content` prose.
interface ChatMessage {
  role: 'user' | 'assistant';
  content?: string;
  blocks?: AskBlock[];
  scope?: AskScope;
  citations?: ArtefactCitation[];
  /** When this answer was generated. Drives the freshness stamp so a reopened old thread shows its
   *  age and the reviewer can re-run for the latest (the claim keeps changing while it's validating). */
  createdAt?: string;
}

function formatConvDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Formatting helpers (Rule Zero: never fabricate — null/undefined/NaN always render as '—') ──

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatAud(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(value);
}

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(1)} hours`;
}

function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(1)}%`;
}

function formatKpiValue(item: { value: number | string; unit?: 'aud' | 'hours' | 'count' | 'text' }): string {
  // Defensive: the API is untyped fetch, so a null/undefined can arrive at runtime even though the
  // mirrored type says `number | string`.
  const raw = item.value as number | string | null | undefined;
  if (raw === null || raw === undefined || raw === '') return '—';
  if (item.unit === 'aud') {
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? formatAud(n) : String(raw);
  }
  if (item.unit === 'hours') {
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? formatHours(n) : String(raw);
  }
  return String(raw);
}

// A bare citation (e.g. a defensibility quote) carries only artefactId/quote/offsets — no title or
// type, since ArtefactCitation is deliberately minimal. We still need an ArtefactRef to open the
// shared PortalArtefactPanel, so we synthesise a generic label. This is a UI label, not a business
// figure, so it does not violate Rule Zero — the panel fetches the artefact's real content/title
// context itself and the pinned "Cited passage" card shows the verbatim quote regardless.
function citationToArtefactRef(citation: ArtefactCitation): ArtefactRef {
  return { id: citation.artefactId, title: 'Cited evidence', type: 'evidence', citation };
}

function evidenceRowToArtefactRef(row: { artefactId: string; title: string; artefactType: string; createdAt: string }): ArtefactRef {
  return { id: row.artefactId, title: row.title, type: row.artefactType, createdAt: row.createdAt };
}

// ── Ledger timeline defensive narrowing (entries are `unknown` — never crash on unexpected shape) ──

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
}

function pickString(rec: Record<string, unknown> | null, key: string): string | null {
  if (!rec) return null;
  const v = rec[key];
  return typeof v === 'string' ? v : null;
}

function describeLedgerEntry(entry: unknown): { status: string; date: string } {
  const rec = asRecord(entry);
  const status = pickString(rec, 'status') ?? pickString(rec, 'label') ?? pickString(rec, 'determination') ?? 'Recorded';
  const dateRaw = pickString(rec, 'date') ?? pickString(rec, 'createdAt') ?? pickString(rec, 'asOf') ?? pickString(rec, 'timestamp');
  return { status, date: dateRaw ? formatDate(dateRaw) : '—' };
}

// ── Scope banner (shown above the transcript / in the empty state) ───────────

function ScopeBanner({ claimInfo, projectCount }: { claimInfo: ClaimInfo; projectCount: number }) {
  return (
    <div className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
        style={{ background: 'linear-gradient(150deg, oklch(0.44 0.09 262), oklch(0.30 0.07 264))' }}
      >
        <EdisonMark size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-snug truncate">{claimInfo.name}</p>
        <p className="text-xs text-gray-500">
          {claimInfo.incomeYear} R&D Tax Incentive claim · {projectCount} project{projectCount !== 1 ? 's' : ''}
        </p>
      </div>
      <span className="flex items-center gap-1.5 text-xs text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2.5 py-1 rounded-full whitespace-nowrap shrink-0">
        <Lock className="h-3 w-3" aria-hidden="true" />
        Read-only · rates withheld
      </span>
    </div>
  );
}

// Persistent scope chip (spec §7) — shown at the top of the transcript, always reflecting the
// scope of the LATEST answer so the auditor can never mistake which FY / project set a figure was
// drawn from. The internal R&D basis mode is deliberately NOT surfaced — it is a platform-internal
// calculation concept the external reviewer is not meant to be aware of.
function ScopeChip({ scope }: { scope: AskScope }) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-gray-800 bg-gray-900 px-3 py-1.5 text-xs text-gray-400 w-fit">
      <ShieldCheck className="h-3.5 w-3.5 text-blue-400 shrink-0" aria-hidden="true" />
      <span>
        Answering within <span className="text-gray-200 font-medium">{scope.incomeYear}</span> ·{' '}
        {scope.projectCount} project{scope.projectCount === 1 ? '' : 's'}
      </span>
    </div>
  );
}

// ── Chat transcript bubbles ────────────────────────────────────────────────────

function UserBubble({ content }: { content: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-lg bg-blue-600 text-white text-sm px-4 py-2.5 rounded-2xl rounded-tr-sm leading-relaxed whitespace-pre-wrap">
        {content}
      </div>
    </div>
  );
}

// Per-answer footer: when the answer was generated + a neutral "re-run for latest". The claim is
// still being finalised, so figures move — this lets a returning reviewer refresh an answer on
// demand (re-run APPENDS a fresh turn; the original is never overwritten). Deliberately framed as
// data-freshness, not "we improved" — we never advertise a fix or tamper with the saved record.
function AnswerFooter({ createdAt, onRerun, disabled }: { createdAt?: string; onRerun?: () => void; disabled?: boolean }) {
  if (!createdAt && !onRerun) return null;
  return (
    <div className="flex items-center gap-3 mt-1 pl-11 text-[11px] text-gray-600">
      {createdAt && <span>Generated {formatDate(createdAt)}</span>}
      {onRerun && (
        <button
          type="button"
          onClick={onRerun}
          disabled={disabled}
          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className="h-3 w-3" aria-hidden="true" /> Re-run for latest
        </button>
      )}
    </div>
  );
}

function EdisonAvatar() {
  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]"
      style={{ background: 'linear-gradient(150deg, oklch(0.44 0.09 262), oklch(0.30 0.07 264))' }}
    >
      <EdisonMark size={16} />
    </div>
  );
}

function AssistantBubble({ content }: { content: string }) {
  return (
    <div className="flex gap-3">
      <EdisonAvatar />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-white">Edison</span>
        </div>
        <PortalMarkdown content={content} />
      </div>
    </div>
  );
}

// ── Typed-block renderers (Task 8) — one component per AskBlock['type'] ───────
//
// Determinism note: every figure below is read straight off the block field and formatted here —
// never recomputed — so what the auditor sees always matches the deterministic runner's output.

type EvidenceRow = Extract<AskBlock, { type: 'evidence_table' }>['rows'][number];

function EvidenceTableBlock({
  rows,
  onOpenArtefact,
}: {
  rows: EvidenceRow[];
  onOpenArtefact: (ref: ArtefactRef) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 text-sm text-gray-500">
        No evidence artefacts matched this question.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-800 text-left text-gray-500">
              <th className="px-3 py-2 font-medium">Title</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Project</th>
              <th className="px-3 py-2 font-medium">Activity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {rows.map((row) => (
              <tr key={row.artefactId}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenArtefact(evidenceRowToArtefactRef(row))}
                      className="text-left text-blue-400 hover:text-blue-300 hover:underline"
                      aria-label={`Open evidence: ${row.title}`}
                    >
                      {row.title}
                    </button>
                    {row.githubUrl && (
                      <a
                        href={row.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-gray-500 hover:text-gray-300 shrink-0"
                        aria-label={`Open ${row.title} on GitHub`}
                      >
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{row.artefactType.replace(/_/g, ' ')}</td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatDate(row.date ?? row.sourceDate ?? row.createdAt)}</td>
                <td className="px-3 py-2 text-gray-400">{row.projectName ?? '—'}</td>
                <td className="px-3 py-2 text-gray-400">{row.activityName ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiBlockView({ block }: { block: Extract<AskBlock, { type: 'kpi_block' }> }) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{block.title}</p>
      {block.items.length === 0 ? (
        <p className="text-sm text-gray-500">No figures available.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {block.items.map((item, i) => (
            <div key={`${item.label}-${i}`} className="rounded-lg bg-gray-800/60 px-3 py-2.5">
              <p className="text-lg font-semibold text-white">{formatKpiValue(item)}</p>
              <p className="text-xs text-gray-500 mt-0.5">{item.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Verified / Unmapped / Not synced — derived from unmapped + glAmount, never invented.
function xeroStatus(row: { glAmount: number | null; unmapped: boolean }): { label: string; cls: string; Icon: typeof CheckCircle2 } {
  if (row.glAmount === null || row.glAmount === undefined) {
    return { label: 'Not synced', cls: 'bg-gray-800 text-gray-400 border border-gray-700', Icon: Clock };
  }
  if (row.unmapped) {
    return { label: 'Unmapped', cls: 'bg-amber-900/50 text-amber-300 border border-amber-800/40', Icon: AlertTriangle };
  }
  return { label: 'Verified', cls: 'bg-green-900/50 text-green-300 border border-green-800/40', Icon: CheckCircle2 };
}

function XeroStatusChip({ row }: { row: { glAmount: number | null; unmapped: boolean } }) {
  const { label, cls, Icon } = xeroStatus(row);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap ${cls}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

function ExpenditureTableBlock({ block }: { block: Extract<AskBlock, { type: 'expenditure_table' }> }) {
  const { labour, nonLabour, totals } = block;
  return (
    <div className="space-y-3">
      {labour.length > 0 && (
        <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
          <p className="px-3 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Labour</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500">
                  <th className="px-3 py-2 font-medium">Project</th>
                  <th className="px-3 py-2 font-medium">Activity</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium text-right">R&D hours</th>
                  <th className="px-3 py-2 font-medium text-right">R&D expenditure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {labour.map((row, i) => (
                  <tr key={`${row.projectName}-${row.activityName}-${i}`}>
                    <td className="px-3 py-2 text-gray-300">{row.projectName}</td>
                    <td className="px-3 py-2 text-gray-400">{row.activityName}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.activityType.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{formatHours(row.rdHours)}</td>
                    <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{formatAud(row.rdExpenditure)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {nonLabour.length > 0 && (
        <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
          <p className="px-3 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Non-labour</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500">
                  <th className="px-3 py-2 font-medium">Vendor</th>
                  <th className="px-3 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 font-medium text-right">Edison $</th>
                  <th className="px-3 py-2 font-medium text-right">GL $</th>
                  <th className="px-3 py-2 font-medium text-right">Variance</th>
                  <th className="px-3 py-2 font-medium">Accounts</th>
                  <th className="px-3 py-2 font-medium">Xero status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {nonLabour.map((row, i) => (
                  <tr key={`${row.vendor}-${i}`}>
                    <td className="px-3 py-2 text-gray-300">{row.vendor}</td>
                    <td className="px-3 py-2 text-gray-400">{row.category ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{formatAud(row.edisonAmount)}</td>
                    <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{formatAud(row.glAmount)}</td>
                    <td className="px-3 py-2 text-right text-gray-400 whitespace-nowrap">{formatPct(row.variancePct)}</td>
                    <td className="px-3 py-2 text-gray-500">{row.mappedAccounts.length > 0 ? row.mappedAccounts.join(', ') : '—'}</td>
                    <td className="px-3 py-2"><XeroStatusChip row={row} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {labour.length === 0 && nonLabour.length === 0 && (
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 text-sm text-gray-500">
          No expenditure recorded for this scope.
        </div>
      )}

      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {([
          ['Total R&D hours', formatHours(totals.totalRdHours)],
          ['Total R&D labour', formatAud(totals.totalRdExpenditure)],
          ['Total non-labour', formatAud(totals.totalNonLabourExpenditure)],
          ['Grand total', formatAud(totals.grandTotal)],
        ] as [string, string][]).map(([label, value]) => (
          <div key={label} className="rounded-lg bg-gray-800/60 px-3 py-2.5">
            <p className="text-sm font-semibold text-white">{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReconciliationTableBlock({ block }: { block: Extract<AskBlock, { type: 'reconciliation_table' }> }) {
  const { nonLabour, meta } = block;
  return (
    <div className="space-y-3">
      {nonLabour.length === 0 ? (
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 text-sm text-gray-500">
          No non-labour reconciliation data available.
        </div>
      ) : (
        <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500">
                  <th className="px-3 py-2 font-medium">Vendor</th>
                  <th className="px-3 py-2 font-medium text-right">Edison $</th>
                  <th className="px-3 py-2 font-medium text-right">GL $</th>
                  <th className="px-3 py-2 font-medium text-right">Variance $</th>
                  <th className="px-3 py-2 font-medium text-right">Variance %</th>
                  <th className="px-3 py-2 font-medium">Accounts</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {nonLabour.map((row, i) => (
                  <tr key={`${row.vendor}-${i}`}>
                    <td className="px-3 py-2 text-gray-300">{row.vendor}</td>
                    <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{formatAud(row.edisonAmount)}</td>
                    <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{formatAud(row.glAmount)}</td>
                    <td className="px-3 py-2 text-right text-gray-400 whitespace-nowrap">{formatAud(row.varianceAmount)}</td>
                    <td className="px-3 py-2 text-right text-gray-400 whitespace-nowrap">{formatPct(row.variancePct)}</td>
                    <td className="px-3 py-2 text-gray-500">{row.mappedAccounts.length > 0 ? row.mappedAccounts.join(', ') : '—'}</td>
                    <td className="px-3 py-2"><XeroStatusChip row={row} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-600">
        {meta.hasSnapshotData ? 'Locked claim snapshot data available. ' : 'No locked claim snapshot recorded. '}
        Last Xero sync: {meta.lastXeroSyncAt ? formatDate(meta.lastXeroSyncAt) : 'never'}.
      </p>
    </div>
  );
}

function ipOwnershipBadge(ipOwnership: string) {
  if (ipOwnership === 'client') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-red-900/60 text-red-300">
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        HIGH RISK — Client IP
      </span>
    );
  }
  if (ipOwnership === 'undetermined') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-900/60 text-amber-300">
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        IP Undetermined
      </span>
    );
  }
  return null;
}

function CriteriaCardsBlock({
  block,
  onOpenArtefact,
}: {
  block: Extract<AskBlock, { type: 'criteria_cards' }>;
  onOpenArtefact: (ref: ArtefactRef) => void;
}) {
  if (block.projects.length === 0) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 text-sm text-gray-500">
        No projects matched this question.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {block.projects.map((project) => {
        const det = determinationBadgeInline(project.tfaDetermination);
        return (
          <div key={project.projectId} className="rounded-xl border border-gray-800 bg-gray-900 p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-white">{project.projectName}</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${det.cls}`}>{det.label}</span>
            </div>

            {project.problemStatement && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Problem statement</p>
                <p className="text-xs text-gray-300 leading-relaxed">{project.problemStatement}</p>
              </div>
            )}

            {project.coreActivities.length > 0 && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">
                  Core activities ({project.coreActivities.length})
                </p>
                <div className="space-y-2">
                  {project.coreActivities.map((act, i) => (
                    <div key={`${act.name}-${i}`} className="rounded-lg bg-gray-800/40 px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-medium text-gray-200">{act.name}</span>
                        {ipOwnershipBadge(act.ipOwnership)}
                      </div>
                      {act.uncertaintyBases.length > 0 ? (
                        <ul className="list-disc pl-4 space-y-0.5">
                          {act.uncertaintyBases.map((basis, bi) => (
                            <li key={bi} className="text-xs text-gray-400 leading-relaxed">{basis}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-600 italic">No confirmed uncertainty basis on record.</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {project.documents.length > 0 ? (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">
                  Documents ({project.documents.length})
                </p>
                <div className="divide-y divide-gray-800 rounded-lg border border-gray-800 overflow-hidden">
                  {project.documents.map((doc) => (
                    <button
                      key={doc.artefactId}
                      type="button"
                      onClick={() => onOpenArtefact({ id: doc.artefactId, title: doc.title, type: doc.artefactType })}
                      className="w-full flex items-center justify-between gap-3 bg-gray-900 hover:bg-gray-800/80 transition-colors px-3 py-2 text-left"
                      aria-label={`Open document: ${doc.title}`}
                    >
                      <span className="text-xs text-blue-400 hover:text-blue-300 truncate">{doc.title}</span>
                      <span className="text-[10px] text-gray-500 shrink-0">{doc.artefactType.replace(/_/g, ' ')}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-600 italic">No documents linked to this project.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Local badge helper for TFA determination (separate name from portal-eligibility's
// determinationBadge since this file has its own dark-surface palette conventions).
function determinationBadgeInline(det: string | null): { label: string; cls: string } {
  if (!det) return { label: 'TFA In Progress', cls: 'bg-gray-700 text-gray-300' };
  if (det === 'uncertain') return { label: 'Uncertain — Eligible', cls: 'bg-green-900/60 text-green-300' };
  if (det === 'partially_uncertain') return { label: 'Partially Uncertain', cls: 'bg-amber-900/60 text-amber-300' };
  if (det === 'certain') return { label: 'Certain — Ineligible', cls: 'bg-red-900/60 text-red-300' };
  return { label: det, cls: 'bg-gray-700 text-gray-300' };
}

function scoreBadge(score: string): { label: string; cls: string } {
  const key = score.toLowerCase();
  if (key === 'strong' || key === 'moderate' || key === 'weak' || key === 'gap') {
    return { label: RATING_LABEL[key as Rating], cls: RATING_BADGE[key as Rating] };
  }
  // A criterion the engine hasn't scored yet ('pending') — neutral, never a red "Gap".
  if (key === 'pending') {
    return { label: 'Not assessed', cls: 'bg-gray-800 text-gray-300 border border-gray-700' };
  }
  return { label: score.charAt(0).toUpperCase() + score.slice(1), cls: 'bg-gray-800 text-gray-300 border border-gray-700' };
}

function DefensibilityBlockView({
  block,
  onOpenArtefact,
}: {
  block: Extract<AskBlock, { type: 'defensibility_block' }>;
  onOpenArtefact: (ref: ArtefactRef) => void;
}) {
  if (block.checks.length === 0) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 text-sm text-gray-500">
        No defensibility checks recorded for {block.projectName}.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 space-y-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{block.projectName} — defensibility</p>
      {block.checks.map((check, i) => {
        const badge = scoreBadge(check.score);
        return (
          <div key={`${check.criterion}-${i}`} className="rounded-lg bg-gray-800/40 px-3 py-2.5 space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-200">{check.criterion.replace(/_/g, ' ')}</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>
              {check.supportingArtefactCount > 0 && (
                <span className="text-[10px] text-gray-500">
                  backed by {check.supportingArtefactCount} artefact{check.supportingArtefactCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">{check.rationale}</p>
            {check.gapsSummary && (
              <p className="text-xs text-amber-300/80 leading-relaxed">Gaps: {check.gapsSummary}</p>
            )}
            {check.citations.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {check.citations.map((c, ci) => (
                  <button
                    key={ci}
                    type="button"
                    onClick={() => onOpenArtefact(citationToArtefactRef(c))}
                    className="w-full text-left rounded-lg border border-gray-800 bg-gray-900 px-3 py-2 hover:border-gray-700 hover:bg-gray-800/70 transition-colors"
                    aria-label={`Open cited evidence at line ${c.line}`}
                  >
                    <p className="text-xs text-gray-300 italic leading-relaxed line-clamp-2">&ldquo;{c.quote}&rdquo;</p>
                    <p className="text-[10px] text-gray-600 mt-1">Line {c.line} · view evidence →</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProjectTableRow({
  row,
}: {
  row: Extract<AskBlock, { type: 'project_table' }>['rows'][number];
}) {
  const [expanded, setExpanded] = useState(false);
  const hasActivities = Boolean(row.activities && row.activities.length > 0);
  const det = determinationBadgeInline(row.tfaDetermination);
  return (
    <>
      <tr>
        <td className="px-3 py-2">
          {hasActivities ? (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="flex items-center gap-1.5 text-gray-200 hover:text-white"
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} activities for ${row.projectName}`}
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
              {row.projectName}
            </button>
          ) : (
            <span className="text-gray-300">{row.projectName}</span>
          )}
        </td>
        <td className="px-3 py-2"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${det.cls}`}>{det.label}</span></td>
        <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{formatHours(row.rdHours)}</td>
        <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{formatAud(row.rdExpenditure)}</td>
      </tr>
      {expanded && hasActivities && row.activities?.map((act, i) => (
        <tr key={`${act.name}-${i}`} className="bg-gray-800/30">
          <td className="px-3 py-1.5 pl-8 text-gray-400 flex items-center gap-2">
            {act.name}
            {ipOwnershipBadge(act.ipOwnership)}
          </td>
          <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{act.activityType.replace(/_/g, ' ')}</td>
          <td className="px-3 py-1.5 text-right text-gray-400 whitespace-nowrap">{formatHours(act.rdHours)}</td>
          <td className="px-3 py-1.5 text-right text-gray-400 whitespace-nowrap">{formatAud(act.rdExpenditure)}</td>
        </tr>
      ))}
    </>
  );
}

function ProjectTableBlock({ block }: { block: Extract<AskBlock, { type: 'project_table' }> }) {
  if (block.rows.length === 0) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 text-sm text-gray-500">
        No projects matched this question.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-800 text-left text-gray-500">
              <th className="px-3 py-2 font-medium">Project</th>
              <th className="px-3 py-2 font-medium">TFA determination</th>
              <th className="px-3 py-2 font-medium text-right">R&D hours</th>
              <th className="px-3 py-2 font-medium text-right">R&D expenditure</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {block.rows.map((row, i) => <ProjectTableRow key={`${row.projectName}-${i}`} row={row} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SdlcCoverageBlock({ block }: { block: Extract<AskBlock, { type: 'sdlc_coverage' }> }) {
  const fmtH = (n: number) => `${n.toLocaleString('en-AU', { maximumFractionDigits: 1 })} h`;
  const coveredCount = block.categories.filter((c) => c.hours > 0).length;
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-800">
        <div className="text-xs font-medium text-gray-300">{block.title}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">
          {coveredCount} of {block.categories.length} standard SDLC stages represented · {block.basisNote}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-800 text-left text-gray-500">
              <th className="px-3 py-2 font-medium">SDLC stage</th>
              <th className="px-3 py-2 font-medium text-right">Recorded hours</th>
              <th className="px-3 py-2 font-medium">Example work</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {block.categories.map((c) => (
              <tr key={c.key} className={c.hours > 0 ? 'text-gray-300' : 'text-gray-600'}>
                <td className="px-3 py-2">{c.label}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtH(c.hours)}</td>
                <td className="px-3 py-2 text-gray-500">{c.exampleLabels.join('; ') || '—'}</td>
              </tr>
            ))}
            {block.unmappedHours > 0 && (
              <tr className="text-gray-600">
                <td className="px-3 py-2 italic">Unmapped / uncategorised</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtH(block.unmappedHours)}</td>
                <td className="px-3 py-2">—</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Stage drill (Task 9/10): the top individual work items within one named SDLC stage. Mirrors
// SdlcCoverageBlock's dark-theme table (same fmtH hours format, same header/row classes) so the two
// read as one system. rawEvidenceAccess-gated on the backend — the block only ever arrives for an
// auditor grant; guard defensively for an empty item list (backend won't emit one, but never crash).
function SdlcStageDetailBlock({ block }: { block: Extract<AskBlock, { type: 'sdlc_stage_detail' }> }) {
  const fmtH = (n: number) => `${n.toLocaleString('en-AU', { maximumFractionDigits: 1 })} h`;
  if (block.items.length === 0) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 text-sm text-gray-500">
        No individual work items recorded for the {block.stageLabel} stage.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-800">
        <div className="text-xs font-medium text-gray-300">{block.stageLabel} — work detail</div>
        <div className="text-[11px] text-gray-500 mt-0.5">{block.basisNote}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-800 text-left text-gray-500">
              <th className="px-3 py-2 font-medium">Work</th>
              <th className="px-3 py-2 font-medium text-right">Recorded hours</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {block.items.map((item, i) => (
              <tr key={`${item.label}-${i}`} className={item.hours > 0 ? 'text-gray-300' : 'text-gray-600'}>
                <td className="px-3 py-2">{item.label}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtH(item.hours)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompositionBlock({ block }: { block: Extract<AskBlock, { type: 'composition' }> }) {
  const fmtH = (n: number) => `${n.toLocaleString('en-AU', { maximumFractionDigits: 1 })} h`;
  const fmtA = (n: number) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-800">
        <div className="text-xs font-medium text-gray-300">{block.title}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">
          {block.claimedProjectCount} projects in this claim · {block.excludedNonRdProjectCount} assessed as non-R&D and excluded
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-800 text-left text-gray-500">
              <th className="px-3 py-2 font-medium">Category</th>
              <th className="px-3 py-2 font-medium text-right">Recorded hours</th>
              <th className="px-3 py-2 font-medium text-right">Expenditure</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {block.categories.map((c) => (
              <tr key={c.key} className={c.hours > 0 ? 'text-gray-300' : 'text-gray-600'}>
                <td className="px-3 py-2">{c.label}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtH(c.hours)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtA(c.expenditure)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LedgerTimelineBlock({ block }: { block: Extract<AskBlock, { type: 'ledger_timeline' }> }) {
  const current = block.current !== null && block.current !== undefined ? describeLedgerEntry(block.current) : null;
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 space-y-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{block.projectName} — eligibility ledger</p>
      {current ? (
        <div className="rounded-lg bg-gray-800/60 px-3 py-2.5">
          <p className="text-xs text-gray-500 mb-0.5">Current</p>
          <p className="text-sm text-white font-medium">{current.status}</p>
          <p className="text-xs text-gray-500">{current.date}</p>
        </div>
      ) : (
        <p className="text-xs text-gray-600 italic">No current ledger entry recorded.</p>
      )}
      {block.history.length > 0 ? (
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">History ({block.history.length})</p>
          <ul className="space-y-1.5">
            {block.history.map((entry, i) => {
              const desc = describeLedgerEntry(entry);
              return (
                <li key={i} className="flex items-center justify-between text-xs text-gray-400 rounded-lg bg-gray-800/30 px-3 py-2">
                  <span>{desc.status}</span>
                  <span className="text-gray-600">{desc.date}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-gray-600 italic">No prior ledger history recorded.</p>
      )}
    </div>
  );
}

function RefusalBlockView({ block }: { block: Extract<AskBlock, { type: 'refusal' }> }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-blue-500/20 bg-blue-500/10 px-4 py-3">
      <Info className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-sm text-blue-200 leading-relaxed">{block.message}</p>
    </div>
  );
}

// Dispatches on block.type — the single place that decides how each typed block renders. A shared
// `onOpenArtefact` drives every evidence/criteria/defensibility row across every block type into
// the ONE PortalArtefactPanel instance the page owns (spec §6).
function AskBlocks({ blocks, onOpenArtefact }: { blocks: AskBlock[]; onOpenArtefact: (ref: ArtefactRef) => void }) {
  return (
    <div className="space-y-3">
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'prose':
            return <PortalMarkdown key={i} content={block.text} />;
          case 'kpi_block':
            return <KpiBlockView key={i} block={block} />;
          case 'evidence_table':
            return <EvidenceTableBlock key={i} rows={block.rows} onOpenArtefact={onOpenArtefact} />;
          case 'expenditure_table':
            return <ExpenditureTableBlock key={i} block={block} />;
          case 'reconciliation_table':
            return <ReconciliationTableBlock key={i} block={block} />;
          case 'criteria_cards':
            return <CriteriaCardsBlock key={i} block={block} onOpenArtefact={onOpenArtefact} />;
          case 'defensibility_block':
            return <DefensibilityBlockView key={i} block={block} onOpenArtefact={onOpenArtefact} />;
          case 'project_table':
            return <ProjectTableBlock key={i} block={block} />;
          case 'sdlc_coverage':
            return <SdlcCoverageBlock key={i} block={block} />;
          case 'sdlc_stage_detail':
            return <SdlcStageDetailBlock key={i} block={block} />;
          case 'composition':
            return <CompositionBlock key={i} block={block} />;
          case 'ledger_timeline':
            return <LedgerTimelineBlock key={i} block={block} />;
          case 'refusal':
            return <RefusalBlockView key={i} block={block} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

function AssistantBlocksBubble({
  blocks,
  onOpenArtefact,
}: {
  blocks: AskBlock[];
  onOpenArtefact: (ref: ArtefactRef) => void;
}) {
  return (
    <div className="flex gap-3">
      <EdisonAvatar />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-white">Edison</span>
        </div>
        <AskBlocks blocks={blocks} onOpenArtefact={onOpenArtefact} />
      </div>
    </div>
  );
}

function AssistantPendingBubble() {
  return (
    <div className="flex gap-3">
      <EdisonAvatar />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-white">Edison</span>
        </div>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Analysing claim data — searching TFA assessments, activities, competence records…
        </div>
      </div>
    </div>
  );
}

function ErrorBubble({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-red-900/50 bg-red-950/40 px-4 py-3 text-sm text-red-300">
      <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
      {message}
      <button onClick={onRetry} className="ml-auto text-xs underline hover:no-underline">Retry</button>
    </div>
  );
}

function HistorySkeleton() {
  return (
    <div className="space-y-6" aria-hidden="true">
      <div className="flex justify-end">
        <Skeleton className="h-9 w-64 rounded-2xl" />
      </div>
      <div className="flex gap-3">
        <Skeleton className="h-8 w-8 rounded-[10px] shrink-0" />
        <div className="flex-1 space-y-2 pt-1">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3.5 w-full" />
          <Skeleton className="h-3.5 w-5/6" />
        </div>
      </div>
    </div>
  );
}

// ── Previous conversations list (home screen) ────────────────────────────────

function PreviousConversations({
  conversations,
  onOpen,
}: {
  conversations: ConversationSummary[];
  onOpen: (id: string) => void;
}) {
  return (
    <div>
      <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2.5">Previous conversations</p>
      <div className="flex flex-col gap-2">
        {conversations.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onOpen(c.id)}
            className="w-full text-left flex items-start gap-3 rounded-xl border border-gray-800 bg-gray-900 px-3.5 py-3 hover:border-gray-700 hover:bg-gray-800/60 transition-colors group"
          >
            <MessageSquare className="h-4 w-4 text-indigo-400 group-hover:text-indigo-300 shrink-0 mt-0.5" aria-hidden="true" />
            <span className="flex-1 min-w-0">
              <span className="block text-xs text-gray-300 group-hover:text-gray-100 transition-colors truncate">{c.title}</span>
              <span className="block text-[11px] text-gray-600 mt-0.5">
                {formatConvDate(c.lastMessageAt)} · {c.messageCount} message{c.messageCount === 1 ? '' : 's'}
              </span>
            </span>
            <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-gray-400 shrink-0 mt-0.5 transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Home screen (hero + suggestions + previous conversations + project cards) ──

function HomeScreen({
  claimInfo, projects, isContextLoading, basePath, conversations, onSuggestion, onOpenConversation,
}: {
  claimInfo: ClaimInfo | null;
  projects: ProjectCardData[];
  isContextLoading: boolean;
  basePath: string;
  conversations: ConversationSummary[];
  onSuggestion: (question: string) => void;
  onOpenConversation: (id: string) => void;
}) {
  return (
    <div className="space-y-5">
      {claimInfo && <ScopeBanner claimInfo={claimInfo} projectCount={projects.length} />}

      {/* Hero */}
      <div className="flex flex-col items-center text-center gap-3 py-4">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-[14px]"
          style={{
            background: 'linear-gradient(150deg, oklch(0.44 0.09 262), oklch(0.30 0.07 264))',
            boxShadow: '0 8px 28px oklch(0.35 0.09 262 / 0.35)',
          }}
        >
          <EdisonMark size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-white">What would you like to verify?</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
            Ask in plain language. Every answer is composed from the underlying evidence — with sources you can open.
          </p>
        </div>
      </div>

      {/* Suggestion chips */}
      <div>
        <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2.5 text-center">Suggested verifications</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => onSuggestion(s.question)}
              className="flex items-start gap-3 rounded-xl border border-gray-800 bg-gray-900 px-3.5 py-3 text-left hover:border-gray-700 hover:bg-gray-800/60 transition-colors group"
            >
              <s.Icon className="h-4 w-4 text-indigo-400 group-hover:text-indigo-300 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="text-xs text-gray-400 group-hover:text-gray-200 transition-colors leading-snug">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Previous conversations */}
      {conversations.length > 0 && (
        <PreviousConversations conversations={conversations} onOpen={onOpenConversation} />
      )}

      {/* Projects in this claim */}
      {!isContextLoading && projects.length > 0 && (
        <div>
          <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2.5">Projects in this claim</p>
          <div className="flex flex-col gap-2">
            {projects.map((p) => (
              <PortfolioProjectCard key={p.projectId} project={p} basePath={basePath} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function PortalAskPage() {
  const token = usePortalStore((s) => s.token);
  const mode = usePortalStore((s) => s.mode);
  const basePath = mode === 'grant' ? '/reviewer/portal' : '/portal';

  const [view, setView] = useState<'home' | 'transcript'>('home');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);

  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [retryQuestion, setRetryQuestion] = useState<string | null>(null);
  const [selectedArtefact, setSelectedArtefact] = useState<ArtefactRef | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [claimInfo, setClaimInfo] = useState<ClaimInfo | null>(null);
  const [projects, setProjects] = useState<ProjectCardData[]>([]);
  const [isContextLoading, setIsContextLoading] = useState(true);

  const fetchContext = useCallback(() => {
    if (!token) { setIsContextLoading(false); return; }
    setIsContextLoading(true);
    getPortalRequestInit(token, mode as PortalMode)
      .then(({ base, headers }) =>
        Promise.all([
          fetch(`${base}/claim`, { headers }).then((r) => r.json()),
          fetch(`${base}/eligibility`, { headers }).then((r) => r.json()),
        ])
      )
      .then(([claimBody, eligBody]: [
        { data?: { name: string; incomeYear: string } },
        { data?: { ungrouped: PortfolioProject[]; byInitiative: Array<{ projects: PortfolioProject[] }> } },
      ]) => {
        if (claimBody.data) {
          setClaimInfo({ name: claimBody.data.name, incomeYear: claimBody.data.incomeYear });
        }
        if (eligBody.data) {
          const all = [
            ...eligBody.data.ungrouped,
            ...eligBody.data.byInitiative.flatMap((g) => g.projects),
          ];
          setProjects(all.filter((p) => p.coreActivities.length > 0).map(summarizeProject));
        }
      })
      .catch(() => { /* context is non-critical */ })
      .finally(() => setIsContextLoading(false));
  }, [token, mode]);

  useEffect(() => { fetchContext(); }, [fetchContext]);

  // Load the list of past conversations for this caller+claim. Non-critical — if it fails, the
  // home screen simply shows no previous-conversations section.
  const fetchConversations = useCallback(() => {
    if (!token) return;
    makePortalInstance(token, { mode })
      .get<AskConversationsResponse>('/api/portal/ask/conversations')
      .then((res) => setConversations(res.data.items))
      .catch(() => { /* non-critical */ });
  }, [token, mode]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Auto-scroll to the newest message (or the pending indicator) as the transcript grows.
  useEffect(() => {
    if (view === 'transcript') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isSending, view]);

  // Ask a question. When activeConversationId is null this starts a NEW conversation — the server
  // mints the id and returns it, which we then adopt so follow-up turns continue the same thread.
  const submit = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || isSending || !token) return;
    setError('');
    setRetryQuestion(null);
    setView('transcript');
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setIsSending(true);
    try {
      const res = await makePortalInstance(token, { mode }).post<AskResponse>('/api/portal/ask', {
        question: trimmed,
        ...(activeConversationId ? { conversationId: activeConversationId } : {}),
      });
      setMessages((prev) => [...prev, {
        role: 'assistant',
        blocks: res.data.blocks,
        scope: res.data.scope,
        citations: res.data.citations,
        createdAt: new Date().toISOString(),
      }]);
      if (!activeConversationId && res.data.conversationId) {
        setActiveConversationId(res.data.conversationId);
      }
    } catch {
      setError('Failed to get an answer. Please try again.');
      setRetryQuestion(trimmed);
    } finally {
      setIsSending(false);
    }
  };

  // Reopen a past conversation from the home list — load its transcript and continue where it left off.
  const openConversation = (id: string) => {
    if (!token || isSending) return;
    setError('');
    setRetryQuestion(null);
    setActiveConversationId(id);
    setMessages([]);
    setView('transcript');
    setIsHistoryLoading(true);
    makePortalInstance(token, { mode })
      .get<AskHistoryResponse>(`/api/portal/ask/history?conversationId=${encodeURIComponent(id)}`)
      .then((res) => {
        setMessages(res.data.messages.map((m): ChatMessage =>
          m.role === 'assistant' && m.blocks
            ? { role: 'assistant', blocks: m.blocks.blocks, citations: m.blocks.citations, scope: m.blocks.scope, createdAt: m.createdAt }
            : { role: m.role, content: m.content, createdAt: m.createdAt },
        ));
      })
      .catch(() => { setError('Failed to load this conversation. Please try again.'); })
      .finally(() => setIsHistoryLoading(false));
  };

  // Return to the home screen and refresh the conversations list so the one just used appears.
  const goHome = () => {
    setView('home');
    setActiveConversationId(null);
    setMessages([]);
    setError('');
    setRetryQuestion(null);
    setInput('');
    fetchConversations();
  };

  // Latest scope across the transcript — drives the persistent scope chip (spec §7). Undefined
  // until the first live answer arrives (history-loaded prose turns carry no scope) — we never
  // show a chip built from stale or fabricated data.
  const latestScope = [...messages].reverse().find((m) => m.role === 'assistant' && m.scope)?.scope;

  const overLimit = input.length > 1000;

  return (
    <div className="w-full">
      {/* Content — the page scrolls as a whole (the audit-portal shell owns the single scroll
          container); the input footer below is `sticky` so it stays reachable at the bottom of
          the viewport without fighting the shell's own layout. */}
      <div className="space-y-6 pb-6">
        {view === 'home' ? (
          <HomeScreen
            claimInfo={claimInfo}
            projects={projects}
            isContextLoading={isContextLoading}
            basePath={basePath}
            conversations={conversations}
            onSuggestion={(q) => void submit(q)}
            onOpenConversation={openConversation}
          />
        ) : (
          <>
            <button
              type="button"
              onClick={goHome}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              Ask Edison
            </button>
            {claimInfo && <ScopeBanner claimInfo={claimInfo} projectCount={projects.length} />}
            {latestScope && <ScopeChip scope={latestScope} />}
            {isHistoryLoading ? (
              <HistorySkeleton />
            ) : (
              <>
                {messages.map((m, i) => {
                  if (m.role === 'user') return <UserBubble key={i} content={m.content ?? ''} />;
                  const prev = messages[i - 1];
                  const question = prev?.role === 'user' ? prev.content : undefined;
                  return (
                    <div key={i}>
                      {m.blocks
                        ? <AssistantBlocksBubble blocks={m.blocks} onOpenArtefact={setSelectedArtefact} />
                        : <AssistantBubble content={m.content ?? ''} />}
                      <AnswerFooter
                        createdAt={m.createdAt}
                        onRerun={question ? () => void submit(question) : undefined}
                        disabled={isSending}
                      />
                    </div>
                  );
                })}
                {isSending && <AssistantPendingBubble />}
                {error && (
                  <ErrorBubble
                    message={error}
                    onRetry={() => { if (retryQuestion) void submit(retryQuestion); }}
                  />
                )}
                {messages.length > 0 && !isSending && !error && (
                  <p className="text-xs text-gray-700">
                    Composed from recorded claim data. Verify against source evidence before relying on this answer.
                  </p>
                )}
              </>
            )}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {selectedArtefact && token && (
        <PortalArtefactPanel
          artefact={selectedArtefact}
          token={token}
          mode={mode}
          onClose={() => setSelectedArtefact(null)}
        />
      )}

      {/* Input — pinned at the bottom of the page via sticky positioning */}
      <div className="sticky bottom-0 bg-gray-950 pt-4 pb-6">
        <form onSubmit={(e) => { e.preventDefault(); void submit(input); }} className="w-full">
          <div
            className="rounded-xl border border-gray-700 bg-gray-900 overflow-hidden focus-within:border-blue-500/60 focus-within:ring-1 focus-within:ring-blue-500/30 transition-colors"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
          >
            <div className="flex items-start gap-3 px-4 pt-4 pb-1">
              <EdisonMark size={16} className="text-indigo-400 mt-1 shrink-0" />
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends (standard chat behaviour); Shift+Enter inserts a newline. Guard
                  // against IME composition so committing a candidate doesn't fire a send.
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    void submit(input);
                  }
                }}
                placeholder="Ask about technical uncertainty, COTS alternatives, researcher roles, IP ownership…"
                rows={3}
                autoFocus
                disabled={isSending}
                className="flex-1 bg-transparent text-sm text-gray-100 placeholder-gray-600 focus:outline-none resize-none disabled:opacity-60"
              />
            </div>
            <div className="flex items-center justify-between px-4 pb-3 mt-1">
              <span className="text-[10px] text-gray-600">
                <kbd className="rounded border border-gray-800 px-1 py-0.5">Enter</kbd> to send ·{' '}
                <kbd className="rounded border border-gray-800 px-1 py-0.5">Shift</kbd>+<kbd className="rounded border border-gray-800 px-1 py-0.5">Enter</kbd> for a new line
              </span>
              <button
                type="submit"
                disabled={!input.trim() || overLimit || isSending}
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {isSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />}
                Ask
              </button>
            </div>
          </div>
        </form>

        <p className="text-center text-xs text-gray-700 pt-3">
          All questions are logged for audit purposes. Edison does not assess eligibility — that is the panel's determination.
        </p>
      </div>
    </div>
  );
}
